/**
 * Push notifications service — uses @capacitor/push-notifications for raw
 * APNs registration. The Cloud Function will need to send via APNs directly
 * (using the .p8 key) instead of FCM topic broadcast.
 *
 * Flow:
 *   1. App load (signed in) → initPushNotifications(uid)
 *   2. Check / request permission
 *   3. Register with APNs → fires `registration` with APNs device token
 *   4. Save token to profiles/{uid}.fcmTokens (we keep the field name for
 *      back-compat, even though it's now an APNs device token, not FCM)
 *   5. Listen for incoming notifications.
 */

import { Capacitor } from "@capacitor/core";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";

import { db } from "../lib/firebase";
import { showInAppBanner } from "../components/NotificationBanner";

let initialized = false;

async function saveTokenToFirestore(userId: string, token: string): Promise<void> {
  try {
    await updateDoc(doc(db, "profiles", userId), {
      fcmTokens: arrayUnion(token),
    });
    console.info("[push] saved APNs token to Firestore");
  } catch (e) {
    console.error("[push] failed to save APNs token:", e);
  }
}

export async function initPushNotifications(
  userId: string,
  opts: { force?: boolean } = {},
): Promise<void> {
  if (initialized && !opts.force) return;
  if (!Capacitor.isNativePlatform()) return;

  let pluginModule: typeof import("@capacitor/push-notifications");
  try {
    pluginModule = await import("@capacitor/push-notifications");
  } catch (e) {
    console.warn("[push] @capacitor/push-notifications not installed:", e);
    return;
  }

  const PushNotifications = pluginModule.PushNotifications;

  try {
    const permStatus = await PushNotifications.checkPermissions();
    let granted = permStatus.receive === "granted";
    if (permStatus.receive === "prompt" || permStatus.receive === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === "granted";
    }
    if (!granted) {
      console.info("[push] permission not granted; skipping registration");
      return;
    }

    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener("registration", async (token) => {
      console.info("[push] APNs device token:", token.value.slice(0, 16) + "...");
      await saveTokenToFirestore(userId, token.value);
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("[push] registration error:", err);
    });

    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.info("[push] foreground notification:", notification);
      showInAppBanner({
        title: notification.title ?? "Notification",
        body: notification.body ?? "",
        data: (notification.data ?? {}) as Record<string, string>,
      });
    });

    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      console.info("[push] user tapped:", action.notification);
      showInAppBanner({
        title: action.notification.title ?? "Notification",
        body: action.notification.body ?? "",
        data: (action.notification.data ?? {}) as Record<string, string>,
      });
    });

    await PushNotifications.register();
    initialized = true;
  } catch (e) {
    console.error("[push] init failed:", e);
  }
}

export async function removePushToken(userId: string, token: string): Promise<void> {
  try {
    const { getDoc, doc: docFn } = await import("firebase/firestore");
    const ref = docFn(db, "profiles", userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const current: string[] = snap.data().fcmTokens ?? [];
    const next = current.filter((t) => t !== token);
    await updateDoc(ref, { fcmTokens: next });
  } catch (e) {
    console.error("[push] removePushToken failed:", e);
  }
}
